import {Attributes, AttributesRow, Button, Card, FlagProps} from 'platform/components';
import {ThemeIconKey} from 'platform/foundation';

import {defaultTo, find, isNil} from 'ramda';

import {CustomerResponseBodyV2} from '@dms/api/customer';
import {CoreExchangeItem, GetWarehousesResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {getCustomerName, useGetVatRatesOptions} from '@dms/shared';
import {getSupplierById, getWarehouseById} from '@dms/warehouse/shared';

import {
  composePath,
  EMPTY_PLACEHOLDER,
  Nullish,
  RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

import {openInNewTab} from '../../../../../utils/openInNewTab';
import {PriceWithAndWithoutVat} from './PriceWithAndWithoutVat';

interface CoreExchangeItemCardProps extends RequiredTestIdProps {
  title: string;
  titleIcon: ThemeIconKey;
  item: CoreExchangeItem | Nullish;
  warehouses: GetWarehousesResponse | Nullish;
  suppliers: CustomerResponseBodyV2[] | Nullish;
  totalPriceLabel: string;
  flags?: FlagProps[];
}

export function CoreExchangeItemCard(props: CoreExchangeItemCardProps) {
  const [vatOptions] = useGetVatRatesOptions();

  const vatRate = find((option) => option.type === props.item?.vatType, vatOptions);

  const openArticleDetail = () =>
    openInNewTab(
      composePath(warehouseRoutes.articleDetailOverview, {
        params: {warehouseId: props.item?.warehouseId, id: props.item?.articleId},
        isSearchParamsPreserved: false,
      })
    );

  const rows: AttributesRow[] = [
    {
      label: i18n.t('entity.warehouse.labels.catalogueNumber'),
      content: (
        <Button
          variant="link"
          size="small"
          title={defaultTo(EMPTY_PLACEHOLDER, props.item?.catalogNo)}
          rightIcon="action/launch"
          isDisabled={isNil(props.item?.warehouseId)}
          onClick={openArticleDetail}
          data-testid={suffixTestId('catalogueNumber', props)}
        />
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.name'),
      value: props.item?.name,
    },
    {
      label: i18n.t('entity.warehouse.labels.warehouse'),
      value: getWarehouseById(props.warehouses, props.item?.warehouseId)?.name,
    },
    {
      label: i18n.t('entity.warehouse.labels.supplier'),
      value: getCustomerName(
        getSupplierById(defaultTo([], props.suppliers), props.item?.supplierId)
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.quantity'),
      value: props.item?.quantity,
    },
    {
      label: i18n.t('general.labels.vatRate'),
      value: isNil(vatRate) ? props.item?.vatType : `${vatRate.rate} %`,
    },
    {
      label: i18n.t('entity.warehouse.labels.unitPrice'),
      content: (
        <PriceWithAndWithoutVat
          withoutVat={props.item?.priceWithoutVat}
          withVat={props.item?.priceWithVat}
          data-testid={suffixTestId('unitPrice', props)}
        />
      ),
    },
    {
      label: props.totalPriceLabel,
      content: (
        <PriceWithAndWithoutVat
          withoutVat={props.item?.valueWithoutVat}
          withVat={props.item?.valueWithVat}
          isBold
          data-testid={suffixTestId('totalPrice', props)}
        />
      ),
    },
  ];

  return (
    <Card
      title={props.title}
      titleIcon={props.titleIcon}
      flags={props.flags}
      variant="inlineGrey"
      data-testid={suffixTestId('card', props)}
    >
      <Attributes rows={rows} size="third" data-testid={suffixTestId('attributes', props)} />
    </Card>
  );
}
